import { Cloud$Country, Cloud$Market } from '../../types/cloudEnums';
import { Otovo$Locale } from '../../types/otovoweb';
import { Sanity$DocumentType } from '../../types/sanityTypes';

type URLConfigEntry = {
  type: Sanity$DocumentType;
  localization: string;
  language: Otovo$Locale;
};

type URLConfig = {
  [market in Cloud$Country]: URLConfigEntry[];
};

const urlConfig: URLConfig = {
  at: [
    { type: 'aboutUs', localization: 'ueber-uns', language: 'de-at' },
    { type: 'productPage', localization: 'produkte', language: 'de-at' },
    { type: 'packagePage', localization: 'komplettset', language: 'de-at' },
    { type: 'pricingPage', localization: 'kosten-garantie', language: 'de-at' },
    { type: 'quotePage', localization: 'angebot', language: 'de-at' },
  ],
  be: [
    { type: 'aboutUs', localization: 'about', language: 'fr-be' },
    { type: 'aboutUs', localization: 'about', language: 'nl-be' },
    { type: 'productPage', localization: 'produits', language: 'fr-be' },
    { type: 'productPage', localization: 'producten', language: 'nl-be' },
  ],
  ch: [
    { type: 'aboutUs', localization: 'ueber-uns', language: 'de-ch' },
    { type: 'aboutUs', localization: 'a-propos', language: 'fr-ch' },
    { type: 'aboutUs', localization: 'chi-siamo', language: 'it-ch' },
    { type: 'productPage', localization: 'produkte', language: 'de-ch' },
    { type: 'productPage', localization: 'produits', language: 'fr-ch' },
    { type: 'productPage', localization: 'prodotti', language: 'it-ch' },
    { type: 'packagePage', localization: 'komplettset', language: 'de-ch' },
    { type: 'packagePage', localization: 'kits-complets', language: 'fr-ch' },
    { type: 'packagePage', localization: 'kit', language: 'it-ch' },
    { type: 'pricingPage', localization: 'kosten-garantie', language: 'de-ch' },
    { type: 'pricingPage', localization: 'prix-garantie', language: 'fr-ch' },
    { type: 'pricingPage', localization: 'prezzo-garanzia', language: 'it-ch' },
    { type: 'quotePage', localization: 'offerte', language: 'de-ch' },
    { type: 'quotePage', localization: 'devis', language: 'fr-ch' },
    { type: 'quotePage', localization: 'preventivo', language: 'it-ch' },
  ],
  de: [
    { type: 'aboutUs', localization: 'ueber-uns', language: 'de-de' },
    { type: 'productPage', localization: 'produkte', language: 'de-de' },
    { type: 'packagePage', localization: 'komplettset', language: 'de-de' },
    { type: 'pricingPage', localization: 'kosten-garantie', language: 'de-de' },
    { type: 'quotePage', localization: 'angebot', language: 'de-de' },
  ],
  es: [
    { type: 'aboutUs', localization: 'sobre-nosotros', language: 'es-es' },
    { type: 'productPage', localization: 'productos', language: 'es-es' },
    {
      type: 'packagePage',
      localization: 'kit-solar-autoconsumo',
      language: 'es-es',
    },
    {
      type: 'pricingPage',
      localization: 'precio-y-garantia-placas-solares',
      language: 'es-es',
    },
    {
      type: 'quotePage',
      localization: 'presupuesto-instalacion-fotovoltaica',
      language: 'es-es',
    },
  ],
  fr: [
    { type: 'aboutUs', localization: 'a-propos', language: 'fr-fr' },
    { type: 'productPage', localization: 'produits', language: 'fr-fr' },
    {
      type: 'packagePage',
      localization: 'les-kits',
      language: 'fr-fr',
    },
    {
      type: 'pricingPage',
      localization: 'prix-garantie',
      language: 'fr-fr',
    },
    {
      type: 'quotePage',
      localization: 'devis',
      language: 'fr-fr',
    },
  ],
  gb: [
    { type: 'aboutUs', localization: 'about', language: 'en-gb' },
    { type: 'productPage', localization: 'products', language: 'en-gb' },
  ],
  it: [
    { type: 'aboutUs', localization: 'chi-siamo', language: 'it-it' },
    { type: 'productPage', localization: 'prodotti', language: 'it-it' },
    {
      type: 'packagePage',
      localization: 'kit-fotovoltaici',
      language: 'it-it',
    },
    { type: 'quotePage', localization: 'preventivo', language: 'it-it' },
    { type: 'pricingPage', localization: 'prezzi-garanzia', language: 'it-it' },
  ],
  no: [
    { type: 'aboutUs', localization: 'om-oss', language: 'nb-no' },
    {
      type: 'packagePage',
      localization: 'pakker',
      language: 'nb-no',
    },
    { type: 'quotePage', localization: 'tilbud', language: 'nb-no' },
    { type: 'pricingPage', localization: 'pris-og-garanti', language: 'nb-no' },
    { type: 'productPage', localization: 'produkter', language: 'nb-no' },
  ],
  nl: [
    { type: 'aboutUs', localization: 'about', language: 'nl-nl' },
    { type: 'productPage', localization: 'producten', language: 'nl-nl' },
  ],
  pl: [
    { type: 'aboutUs', localization: 'o-nas', language: 'pl-pl' },
    { type: 'productPage', localization: 'produkty', language: 'pl-pl' },
    {
      type: 'packagePage',
      localization: 'zestawy',
      language: 'pl-pl',
    },
    { type: 'quotePage', localization: 'wycena', language: 'pl-pl' },
    {
      type: 'pricingPage',
      localization: 'cena-i-gwarancja',
      language: 'pl-pl',
    },
  ],
  pt: [
    { type: 'aboutUs', localization: 'sobre-nos', language: 'pt-pt' },
    { type: 'productPage', localization: 'productos', language: 'pt-pt' },
    {
      type: 'packagePage',
      localization: 'kits-energia',
      language: 'pt-pt',
    },
    { type: 'quotePage', localization: 'orcamento', language: 'pt-pt' },
    { type: 'pricingPage', localization: 'preco-garantia', language: 'pt-pt' },
  ],
  se: [
    { type: 'aboutUs', localization: 'om-oss', language: 'sv-se' },
    { type: 'productPage', localization: 'produkter', language: 'sv-se' },
    {
      type: 'packagePage',
      localization: 'paket',
      language: 'sv-se',
    },
    {
      type: 'pricingPage',
      localization: 'pris-garanti',
      language: 'sv-se',
    },
    {
      type: 'quotePage',
      localization: 'offert',
      language: 'sv-se',
    },
  ],
};

type PathToPageType = {
  from: string;
  to: Sanity$DocumentType;
};
const PATH_TO_PAGE_TYPE: PathToPageType[] = [
  { from: '/about', to: 'aboutUs' },
  { from: '/products', to: 'productPage' },
  { from: '/packages', to: 'packagePage' },
  { from: '/get-a-quote', to: 'quotePage' },
  { from: '/pricing', to: 'pricingPage' },
];

type URLConfigByMarket = {
  nextPath: string;
  localizedPath: string;
};

// Returns the mapping between the path in Next.js and the Sanity localization,
// if there is a mapping. The mapping works both ways.
//
// For example, if the nextPath is '/products/batteria', we'll look for a
// localization for the 'productPage' type in Sanity for the given market and
// return:
//
// {
//   nextPath: '/products',
//   localizedPath: '/prodotti'
// }
//
// Since the mapping works both ways, we'll also return that object when the
// nextPath is the localized variant ('/prodotti/batteria').
//
// null is returned if there is no mapping for the given path and market.
export function getURLConfigForPath(
  nextPath: string,
  market: Cloud$Market,
  locale: Otovo$Locale,
): URLConfigByMarket | null {
  const urls = urlConfig[market];
  if (!urls) {
    return null;
  }

  function getPageType() {
    // Check if the path is the non-localized variant (for example "/products")
    const pageType = PATH_TO_PAGE_TYPE.find((item) =>
      nextPath.startsWith(item.from),
    );

    if (pageType) {
      return pageType;
    }

    // Check if the path is the localized variant (for example "/produkter")

    const withoutLeadingSlash = nextPath.replace(/^\//, '');

    const sanityURLConfig = urls.find((url) =>
      withoutLeadingSlash.startsWith(url.localization),
    );

    if (!sanityURLConfig) {
      return null;
    }

    return PATH_TO_PAGE_TYPE.find((item) => item.to === sanityURLConfig.type);
  }

  const pageType = getPageType();

  if (!pageType) {
    return null;
  }

  const sanityUrlConfig = urls.find(
    (url) => url.type === pageType.to && url.language === locale,
  );

  if (!sanityUrlConfig) {
    return null;
  }

  return {
    nextPath: pageType.from,
    localizedPath: `/${sanityUrlConfig.localization}`,
  };
}

export function getURLConfigForPageType(
  pageType: Sanity$DocumentType,
  market: Cloud$Market,
  locale: Otovo$Locale,
): URLConfigByMarket | null {
  const urls = urlConfig[market];
  if (!urls) {
    return null;
  }

  const sanityUrlConfig = urls.find(
    (url) => url.type === pageType && url.language === locale,
  );

  if (!sanityUrlConfig) {
    return null;
  }
  const pathToPageType = PATH_TO_PAGE_TYPE.find((item) => item.to === pageType);
  if (!pathToPageType) {
    return null;
  }
  return {
    nextPath: pathToPageType.from,
    localizedPath: `/${sanityUrlConfig.localization}`,
  };
}
